import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { finalize, take, tap } from 'rxjs/operators';

import {
  DialogBase,
  TiimeAlertModule,
  TiimeDialogModule,
  TiimeProgressSpinnerModule,
  TiimeSnackbarService
} from 'tiime-material';

import { BusinessUserApiService } from '@api-services/business-user-api.service';
import { CompanyApiService } from '@api-services/company-api.service';
import { AppStoreState } from '@interfaces/app-store-state';
import { BusinessUnit } from '@models/business-unit';
import { BusinessUser } from '@models/business-user';
import { Company } from '@models/company';
import { businessUserSelector } from '@modules/core/store/business-user/business-user-selector';
import { ChooseCompanyCreateTypeResultType } from '@shared-dialogs/choose-company-creation-type-dialog/choose-company-create-type-result.type';

import { CreateCompanyDialogData } from '../create-company-dialog/create-company-dialog-data';
import { CreateCompanyDialogService } from '../create-company-dialog/create-company-dialog.service';
import { CreateCompanyStateMode } from '../create-company-dialog/create-company-state.factory';
import { CreateDemoCompanyDialogData } from '../create-demo-company-dialog/create-demo-company-dialog-data';
import { CreateDemoCompanyDialogService } from '../create-demo-company-dialog/create-demo-company-dialog.service';

@Component({
  standalone: true,
  selector: 'app-choose-company-creation-type-dialog',
  templateUrl: './choose-company-creation-type-dialog.component.html',
  styleUrls: ['./choose-company-creation-type-dialog.component.scss'],
  imports: [CommonModule, TiimeDialogModule, TiimeAlertModule, TiimeProgressSpinnerModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChooseCreationTypeDialogComponent
  extends DialogBase<ChooseCreationTypeDialogComponent, ChooseCompanyCreateTypeResultType>
  implements OnInit
{
  readonly businessUser: WritableSignal<BusinessUser | null> = signal<BusinessUser | null>(null);
  readonly availableBusinessUnits = signal<BusinessUnit[]>([]);
  readonly availableBusinessUnitsForDemo = computed(() =>
    this.availableBusinessUnits().filter((bu: BusinessUnit) => bu.usedForDemo)
  );
  readonly canCreateCompany = computed(() => this.availableBusinessUnits()?.length > 0);
  readonly canCreateDemoCompany: Signal<boolean> = computed(() => this.availableBusinessUnitsForDemo().length > 0);

  constructor(
    dialogRef: MatDialogRef<ChooseCreationTypeDialogComponent>,
    private readonly store: Store<AppStoreState>,
    private readonly createCompanyDialogService: CreateCompanyDialogService,
    private readonly createDemoCompanyDialogService: CreateDemoCompanyDialogService,
    private readonly businessUserApiService: BusinessUserApiService,
    private readonly companyApiService: CompanyApiService,
    private readonly snackbarService: TiimeSnackbarService
  ) {
    super(dialogRef);
  }

  ngOnInit(): void {
    this.initAvailableBusinessUnits();
  }

  openCreateCompanyDialog(mode: CreateCompanyStateMode): void {
    const data: CreateCompanyDialogData = {
      mode,
      businessUnits: this.availableBusinessUnits()
    };

    this.createCompanyDialogService
      .open({ data })
      .beforeClosed()
      .pipe(tap((company: Company) => this.close({ company, isCapitalDeposit: mode === 'CapitalDeposit' })))
      .subscribe();
  }

  openCreateDemoCompanyDialog(): void {
    if (this.availableBusinessUnitsForDemo().length === 1) {
      this.isLoading.set(true);

      const buId: number = this.availableBusinessUnitsForDemo()[0].id;
      this.companyApiService
        .createDemoCompany(buId)
        .pipe(
          tap((company: Company) => {
            this.snackbarService.openSuccess('Le dossier a bien été ajouté');
            this.close({ company, isCapitalDeposit: false });
          }),
          finalize(() => this.isLoading.set(false))
        )
        .subscribe();
    } else {
      const data: CreateDemoCompanyDialogData = {
        businessUnits: this.availableBusinessUnitsForDemo()
      };

      this.createDemoCompanyDialogService
        .open({ data })
        .beforeClosed()
        .pipe(tap((company: Company) => this.close({ company, isCapitalDeposit: false })))
        .subscribe();
    }
  }

  private initAvailableBusinessUnits(): void {
    this.isLoading.set(true);
    combineLatest([this.getCurrentBusinessUser(), this.getAvailableBusinessUnits()])
      .pipe(
        tap(([businessUser, businessUnits]) => {
          this.businessUser.set(businessUser);
          this.availableBusinessUnits.set(businessUnits);
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();
  }

  private getAvailableBusinessUnits(): Observable<BusinessUnit[]> {
    return this.businessUserApiService.getBusinessUserBusinessUnits(true);
  }

  private getCurrentBusinessUser(): Observable<BusinessUser> {
    return this.store.pipe(select(businessUserSelector), take(1));
  }
}
